import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import "../styles/download.css";
import MouseFooter from "../components/MouseFooter";

// Bump this on release — every filename and instruction on the page follows it.
const RELEASE = "0.3.56";
const VERSION = `v${RELEASE}`;

const LINUX_APPIMAGE = `ego-desktop_${RELEASE}_amd64.AppImage`;

const DOWNLOADS = [
  { os: "windows", label: "Windows", sub: "Installer (.msi)", file: `Ego.Desktop_${RELEASE}_x64_en-US.msi` },
  { os: "apple", label: "macOS — Apple Silicon", sub: "M1 / M2 / M3 / M4 (.dmg)", file: `Ego.Desktop_${RELEASE}_aarch64.dmg` },
  { os: "apple", label: "macOS — Intel", sub: "Intel chip (.dmg)", file: `Ego.Desktop_${RELEASE}_x64.dmg` },
  { os: "linux", label: "Linux", sub: "Universal (.AppImage)", file: LINUX_APPIMAGE },
];

function OsIcon({ os }) {
  if (os === "windows") {
    return (
      <svg className="dl-os-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M3 5.4 10.2 4.4v6.9H3V5.4Zm0 13.2 7.2 1v-6.8H3v5.8Zm8-13.9L21 3.2v8.1h-10V4.7Zm0 14.6L21 20.8v-8.4h-10v6.9Z" />
      </svg>
    );
  }
  if (os === "apple") {
    return (
      <svg className="dl-os-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M16.36 12.78c-.02-2.07 1.69-3.06 1.77-3.11-.96-1.41-2.46-1.6-3-1.62-1.27-.13-2.49.75-3.14.75-.65 0-1.65-.73-2.71-.71-1.39.02-2.68.81-3.4 2.06-1.45 2.52-.37 6.25 1.04 8.29.69 1 1.51 2.12 2.58 2.08 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.71.65 1.12-.02 1.83-1.02 2.51-2.02.79-1.16 1.12-2.28 1.13-2.34-.02-.01-2.17-.83-2.19-3.29ZM14.3 6.34c.57-.69.96-1.65.85-2.61-.83.03-1.83.55-2.42 1.24-.53.61-.99 1.59-.86 2.53.92.07 1.86-.47 2.43-1.16Z" />
      </svg>
    );
  }
  if (os === "github") {
    return (
      <svg className="dl-os-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56 0-.28-.01-1.02-.02-2-3.2.7-3.88-1.54-3.88-1.54-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.12 3.05.74.8 1.18 1.83 1.18 3.09 0 4.42-2.69 5.39-5.25 5.68.41.36.78 1.06.78 2.14 0 1.55-.02 2.8-.02 3.18 0 .31.21.67.8.56A11.51 11.51 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5Z" />
      </svg>
    );
  }
  // The drawn Tux, with the black backdrop of the original lifted from the outside in so
  // the line work inside him survives. An image rather than a glyph, so he does not take
  // currentColor — he is white line art and the row sits on a dark ground either way.
  // A shade smaller than the glyph marks: he is line art that fills his square, where they
  // are silhouettes with air around them, so matching box sizes makes him the loud one.
  return (
    <img
      className="dl-os-icon"
      src="/linux.png"
      alt=""
      aria-hidden="true"
      style={{ width: "1.3rem", height: "1.3rem" }}
    />
  );
}

function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);
  return (
    <>
      <header className="navbar relative z-10">
        <div className="logo">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/ego.png" alt="Ego Blockchain Logo" className="logo-img" />
          </Link>
        </div>
        <button className={`hamburger ${menuOpen ? "active" : ""}`} onClick={() => setMenuOpen(!menuOpen)}>
          <span></span><span></span><span></span>
        </button>
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
          <Link to="/download" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Download Ego Desktop</Link>
          <a href="https://chromewebstore.google.com/detail/jpifpbadfhhfapcgkilnmlpfjjdjakja" target="_blank" rel="noopener noreferrer">Ego Wallet for Chrome</a>
          <Link to="/blog" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Blog</Link>
          <Link to="/docs" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Docs</Link>
          <Link to="/egoscan" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>EgoScan</Link>
          <Link to="/claim" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Claim EGOC</Link>
        </nav>
      </header>
      {menuOpen && <div className="menu-overlay" onClick={() => setMenuOpen(false)} />}
    </>
  );
}

export default function Download() {
  const [unavailable, setUnavailable] = useState(null);
  const [checking, setChecking] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // A build that hasn't been uploaded to /Assets/ does not 404. The SPA rewrite
  // answers with index.html and a 200, so the browser cheerfully saves the
  // homepage under a .AppImage name and the user only finds out when running it
  // prints a shell syntax error. Check what came back before handing it over.
  async function startDownload(event, file) {
    event.preventDefault();
    const url = `/Assets/${file}`;

    const save = () => {
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    setUnavailable(null);
    setChecking(file);
    try {
      const res = await fetch(url, { method: "HEAD" });
      const type = res.headers.get("content-type") || "";
      if (res.status === 405) {
        save(); // host doesn't answer HEAD — can't verify, don't block the user
      } else if (!res.ok || type.includes("text/html")) {
        setUnavailable(file);
      } else {
        save();
      }
    } catch {
      save(); // network/CORS hiccup — fall back rather than block a real download
    } finally {
      setChecking(null);
    }
  }

  return (
    <>
      <div className="dl-bg" />
      <Nav />

      <div className="dl-wrap">
        <div className="dl-head">
          <h1>Download Ego Desktop</h1>
          <br />
          <span className="dl-version">{VERSION}</span>
          <p className="dl-lead">
            The full Ego node — wallet, validator, storage and compute, all in one app.
            Click your platform to download.
          </p>
        </div>

        {/* Download links */}
        <div className="dl-links">
          {DOWNLOADS.map((d) => (
            <div className="dl-row" key={d.file}>
              <div className="dl-row-info">
                <span className="dl-os-badge"><OsIcon os={d.os} /></span>
                <span className="dl-row-text">
                  <span className="dl-row-os">{d.label}</span>
                  <span className="dl-row-sub">{d.sub}</span>
                </span>
              </div>
              <a
                className="dl-row-btn"
                href={`/Assets/${d.file}`}
                download
                onClick={(e) => startDownload(e, d.file)}
              >
                {checking === d.file ? "Checking…" : "Download ↓"}
              </a>
            </div>
          ))}
        </div>

        {unavailable && (
          <p className="dl-lead" style={{ color: "#f59e0b", marginTop: 16 }}>
            <strong>{unavailable}</strong> isn’t on the server yet — the {VERSION} build
            for this platform hasn’t been uploaded. Downloading it now would save a web
            page instead of the app, so the download was stopped. Please try again later
            or pick another platform.
          </p>
        )}

        {/* Source */}
        <section className="dl-section">
          <h2>Or build it yourself</h2>
          <p className="dl-lead">
            Every line of Ego is public. The installers above are built from this repository,
            and you can read it, audit it, or compile your own copy instead of trusting ours.
          </p>
          <div className="dl-links">
            <div className="dl-row">
              <div className="dl-row-info">
                <span className="dl-os-badge"><OsIcon os="github" /></span>
                <span className="dl-row-text">
                  <span className="dl-row-os">Source code</span>
                  <span className="dl-row-sub">github.com/ego-blockchain/ego-blockchain-</span>
                </span>
              </div>
              <a
                className="dl-row-btn"
                href="https://github.com/ego-blockchain/ego-blockchain-"
                target="_blank"
                rel="noopener noreferrer"
              >
                View on GitHub ↗
              </a>
            </div>
          </div>
        </section>

        {/* Browser extension */}
        <section className="dl-section">
          <h2>Also available: Ego Wallet for Chrome</h2>
          <p className="dl-lead">
            A lightweight, non‑custodial browser wallet for connecting to dApps —
            no need to run the full desktop node.
          </p>
          <div className="dl-links">
            <div className="dl-row">
              <div className="dl-row-info">
                <span className="dl-row-text">
                  <span className="dl-row-os">Ego Wallet</span>
                  <span className="dl-row-sub">Chrome Web Store extension</span>
                </span>
              </div>
              <a
                className="dl-row-btn"
                href="https://chromewebstore.google.com/detail/jpifpbadfhhfapcgkilnmlpfjjdjakja"
                target="_blank"
                rel="noopener noreferrer"
              >
                Add to Chrome ↓
              </a>
            </div>
          </div>
        </section>

        {/* First-launch security warnings */}
        <section className="dl-section">
          <h2>First launch: your system may warn you</h2>
          <p className="dl-lead">
            Ego Desktop is brand new and not yet code‑signed/notarized by a large vendor, so
            Windows and macOS will show a one‑time security prompt. This is expected — the steps
            below let you run it safely.
          </p>

          <div className="dl-window">
            <div className="dl-window-bar">
              <div className="dl-dots"><span className="r" /><span className="y" /><span className="g" /></div>
              <span className="dl-window-title">🪟 Windows — Microsoft Defender SmartScreen</span>
            </div>
            <div className="dl-window-body">
              <p>
                When you open the installer, Windows may show <strong>“Windows protected your PC”</strong>. To continue:
              </p>
              <ol className="dl-steps">
                <li>Click <strong>More info</strong>.</li>
                <li>Click <strong>Run anyway</strong>, then follow the installer.</li>
              </ol>
            </div>
          </div>

          <div className="dl-window">
            <div className="dl-window-bar">
              <div className="dl-dots"><span className="r" /><span className="y" /><span className="g" /></div>
              <span className="dl-window-title">🍎 macOS — Gatekeeper / quarantine</span>
            </div>
            <div className="dl-window-body">
              <p>
                macOS may say the app <strong>“cannot be opened”</strong> or is from an{" "}
                <strong>unidentified developer</strong> (and Intel builds can show{" "}
                <strong>“is damaged”</strong>) because the app isn’t notarized yet. Two ways to open it:
              </p>

              <span className="dl-optlabel">Option 1 — Right‑click to open</span>
              <ol className="dl-steps">
                <li>Drag <strong>Ego Desktop</strong> into your <strong>Applications</strong> folder.</li>
                <li>Right‑click (or Control‑click) the app → <strong>Open</strong> → <strong>Open</strong>. You only need to do this once.</li>
              </ol>

              <span className="dl-optlabel">Option 2 — Remove the quarantine flag in Terminal</span>
              <p>Use this if it’s still blocked or shows “damaged”:</p>
              <code className="dl-code">xattr -dr com.apple.quarantine "/Applications/Ego Desktop.app"</code>
              <p>Then open the app normally.</p>
            </div>
          </div>
        </section>

        {/* Video walkthrough */}
        <section className="dl-section">
          <h2>Video walkthrough</h2>
          <p className="dl-lead">
            Prefer to follow along? This short video shows how to install and get past the
            Windows / macOS security prompts.
          </p>
          <div className="dl-video">
            <iframe
              src="https://www.youtube.com/embed/bBM0o2rTp0A"
              title="Ego Desktop install walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>

        {/* Install instructions */}
        <section className="dl-section">
          <h2>Install instructions</h2>

          <div className="dl-note">
            <h3>Windows</h3>
            <p>Download the <strong>.msi</strong> (recommended) and run it — follow the installer wizard. If SmartScreen appears, use <strong>More info → Run anyway</strong> as above.</p>
          </div>

          <div className="dl-note">
            <h3>macOS</h3>
            <ol>
              <li>Download the <strong>.dmg</strong> for your chip (Apple Silicon or Intel).</li>
              <li>Open it and drag <strong>Ego Desktop</strong> into <strong>Applications</strong>.</li>
              <li>First launch: <strong>right‑click → Open → Open</strong> (or use the <code className="dl-inline">xattr</code> command above).</li>
            </ol>
          </div>

          <div className="dl-note">
            <h3>Linux</h3>
            <code className="dl-code">chmod +x {LINUX_APPIMAGE}
./{LINUX_APPIMAGE}</code>
          </div>
        </section>
      </div>

      <MouseFooter />
    </>
  );
}